import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { motion } from 'framer-motion'
import GroupsIcon from '@mui/icons-material/Groups'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { TenantSettingsContext, UserContext } from '../../../../App'
import API from '../../../../services/API'
import OverviewHero from './OverviewHero'
import StatTile from './StatTile'
import { IncidentsTrendChart, ShiftTempoChart } from './TrendCharts'
import { FleetReadinessCard } from './HealthCards'
import KPIPulseCard from './KPIPulseCard'
import AttentionRail from './AttentionRail'
import { viz } from './overviewTheme'
import moment from 'moment'

const sectionMotion = (index) => ({
  component: motion.div,
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay: 0.08 * index, ease: 'easeOut' },
})

const ManagerOverview = () => {
  const { user } = useContext(UserContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)

  const canView =
    !!user?.security?.isSiteAdmin ||
    ['Manager', 'Supervisor'].some((team) =>
      user?.employee?.teams?.includes(team),
    )

  useEffect(() => {
    if (!canView) return
    API.get('/manager-overview')
      .then((res) => {
        if (res.status === 200) setData(res.data)
      })
      .catch((e) => {
        console.error(e)
        setError(true)
      })
  }, [canView])

  if (!canView) {
    return (
      <Alert severity="warning">
        The global overview is only available to the Manager and Supervisor
        teams.
      </Alert>
    )
  }

  if (error) {
    return (
      <Alert severity="error">
        We couldn't load the global overview. Please try again shortly.
      </Alert>
    )
  }

  if (!data) {
    return (
      <Stack gap={2}>
        <Skeleton variant="rounded" height={220} />
        <Grid container spacing={2}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={6} sm={4} lg={2} key={i}>
              <Skeleton variant="rounded" height={96} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Skeleton variant="rounded" height={300} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Skeleton variant="rounded" height={300} />
          </Grid>
        </Grid>
      </Stack>
    )
  }

  const { workforce, operations, governance } = data

  return (
    <Stack gap={2.5} pb={4}>
      <OverviewHero
        orgSettings={orgSettings}
        firstName={user?.firstName}
        data={data}
      />

      <Box {...sectionMotion(1)}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={GroupsIcon}
              label="Live staff"
              value={workforce.liveStaff}
              hint={`+ ${workforce.newStarters} new starter${
                workforce.newStarters === 1 ? '' : 's'
              }`}
              url="/employeesdashboard"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={EventAvailableIcon}
              label="Shifts next 7 days"
              value={operations.shiftsNext7}
              hint={`${operations.shiftsToday} today`}
              url="/global-roster"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={EventBusyIcon}
              label="Uncovered shifts"
              value={operations.uncoveredNext7}
              hint="Next 7 days"
              accent={operations.uncoveredNext7 > 0 ? viz.warning : viz.good}
              url="/global-roster"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={GppMaybeIcon}
              label="Open incidents"
              value={governance.openIncidents}
              hint={`${governance.incidents30Days} reported in 30 days`}
              accent={governance.openIncidents > 0 ? viz.warning : viz.good}
              url="/incidents"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={MedicalInformationIcon}
              label="Patient records"
              value={operations.epcrs30Days}
              hint="Submitted in 30 days"
              url="/patient-report-forms"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={PersonAddIcon}
              label="Shift applications"
              value={operations.pendingShiftApplications}
              hint="Awaiting review"
              accent={
                operations.pendingShiftApplications > 0 ? '#268EBB' : viz.good
              }
              url="/shift-applications"
            />
          </Grid>
        </Grid>
      </Box>

      <Box {...sectionMotion(2)}>
        <KPIPulseCard />
      </Box>

      <Box {...sectionMotion(3)}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ShiftTempoChart shiftsByWeek={operations.shiftsByWeek} />
              </Grid>
              <Grid item xs={12} md={6}>
                <IncidentsTrendChart
                  incidentsByMonth={governance.incidentsByMonth}
                />
              </Grid>
              <Grid item xs={12}>
                <FleetReadinessCard fleet={data.fleet} />
              </Grid>
              <Grid item xs={12}>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography variant="smallGrey" textAlign="right">
                      Snapshot generated{' '}
                      {moment(data.generatedAt).from()}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={4}>
            <AttentionRail data={data} />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  )
}

export default ManagerOverview
